/**
 * useTrading — Paper + Live trading actions + derived state.
 * The tick engine (limit fills + PnL updates) lives in useTradingEngine,
 * called ONCE from App DataLayer so there is exactly one interval.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTerminalStore } from '../stores/terminal.store.js';
import { generateId } from '@arc/shared';
import type { Order, Position, OrderSide, OrderType } from '@arc/shared';
import {
  placeHLOrder,
  getAssetIndex,
  hlFormatSize,
  hlFormatPrice,
} from '../services/hyperliquidOrderService.js';
import {
  executeJupiterSwap,
  getJupiterQuote,
  symbolToMint,
  MINT,
} from '../services/jupiterSwapService.js';

// ─── Tick engine (call once in DataLayer) ─────────────────────────────────────

export function useTradingEngine() {
  const prices                  = useTerminalStore(s => s.prices);
  const orders                  = useTerminalStore(s => s.orders);
  const fillOrder               = useTerminalStore(s => s.fillOrder);
  const openPosition            = useTerminalStore(s => s.openPosition);
  const checkAndClosePositions  = useTerminalStore(s => s.checkAndClosePositions);
  const paperAccount            = useTerminalStore(s => s.paperAccount);
  const riskSettings            = useTerminalStore(s => s.riskSettings);
  const setCircuitBreaker       = useTerminalStore(s => s.setCircuitBreaker);
  const circuitBreakerActive    = useTerminalStore(s => s.circuitBreakerActive);

  const pricesRef  = useRef(prices);
  const ordersRef  = useRef(orders);
  const paRef      = useRef(paperAccount);
  const rsRef      = useRef(riskSettings);
  const cbRef      = useRef(circuitBreakerActive);
  useEffect(() => { pricesRef.current = prices;             }, [prices]);
  useEffect(() => { ordersRef.current = orders;             }, [orders]);
  useEffect(() => { paRef.current     = paperAccount;       }, [paperAccount]);
  useEffect(() => { rsRef.current     = riskSettings;       }, [riskSettings]);
  useEffect(() => { cbRef.current     = circuitBreakerActive; }, [circuitBreakerActive]);

  useEffect(() => {
    const interval = setInterval(() => {
      const curPrices = pricesRef.current;

      // Combined price update + SL/TP auto-close (one atomic store set)
      checkAndClosePositions(curPrices);

      // Check pending limit orders for fills (paper mode only)
      const pendingLimits = ordersRef.current.filter(
        o => o.status === 'pending' && o.type === 'limit' && o.mode === 'paper'
      );
      for (const order of pendingLimits) {
        const curPrice = curPrices[order.symbol]?.priceUsd;
        if (!curPrice || !order.price) continue;
        const shouldFill =
          (order.side === 'buy'  && curPrice <= order.price) ||
          (order.side === 'sell' && curPrice >= order.price);
        if (shouldFill) {
          fillOrder(order.id, curPrice);
          openPosition(buildPosition(order, curPrice));
        }
      }

      // Circuit breaker: check daily loss
      const rs = rsRef.current;
      if (rs.circuitBreakerEnabled && rs.maxDailyLossPct > 0) {
        const todayStart  = new Date(); todayStart.setHours(0,0,0,0);
        const todayMs     = todayStart.getTime();
        const todayOrders = ordersRef.current.filter(
          o => o.status === 'filled' && (o.closedAt ?? 0) >= todayMs && o.pnl !== undefined
        );
        const todayPnl    = todayOrders.reduce((s, o) => s + (o.pnl ?? 0), 0);
        const equity      = paRef.current.equity;
        const lossPct     = equity > 0 ? Math.abs(Math.min(0, todayPnl)) / equity * 100 : 0;
        const shouldTrip  = lossPct >= rs.maxDailyLossPct;
        if (shouldTrip !== cbRef.current) {
          setCircuitBreaker(shouldTrip);
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [checkAndClosePositions, fillOrder, openPosition, setCircuitBreaker]);
}

// ─── Trading actions + derived state ──────────────────────────────────────────

export function useTrading() {
  const tradeMode        = useTerminalStore(s => s.tradeMode);
  const prices           = useTerminalStore(s => s.prices);
  const orders           = useTerminalStore(s => s.orders);
  const positions        = useTerminalStore(s => s.positions);
  const paperAccount     = useTerminalStore(s => s.paperAccount);
  const liveAccount      = useTerminalStore(s => s.liveAccount);
  const activeWallet     = useTerminalStore(s => s.activeWallet);
  const placeOrder       = useTerminalStore(s => s.placeOrder);
  const fillOrder        = useTerminalStore(s => s.fillOrder);
  const openPosition     = useTerminalStore(s => s.openPosition);
  const closePosition    = useTerminalStore(s => s.closePosition);
  const cancelOrder      = useTerminalStore(s => s.cancelOrder);
  const resetPaperAccount = useTerminalStore(s => s.resetPaperAccount);

  const [isSubmitting,   setIsSubmitting]   = useState(false);
  const [lastTradeError, setLastTradeError] = useState<string | null>(null);

  const pricesRef = useRef(prices);
  const posRef    = useRef(positions);
  useEffect(() => { pricesRef.current = prices;    }, [prices]);
  useEffect(() => { posRef.current    = positions; }, [positions]);

  // ── Submit order (paper or live) ──────────────────────────────────────────

  const setPositionSLTP = useTerminalStore(s => s.setPositionSLTP);
  const riskSettings    = useTerminalStore(s => s.riskSettings);
  const circuitBreakerActive = useTerminalStore(s => s.circuitBreakerActive);

  const submitOrder = useCallback(async (params: {
    symbol:      string;
    side:        OrderSide;
    type:        OrderType;
    size:        number;
    leverage:    number;
    price?:      number;
    stopLoss?:   number;
    takeProfit?: number;
  }) => {
    const { symbol, side, type, size, leverage, price, stopLoss, takeProfit } = params;
    const curPrice = pricesRef.current[symbol]?.priceUsd;
    if (!curPrice) {
      setLastTradeError(`No price data for ${symbol}`);
      return null;
    }

    // ── Paper mode ─────────────────────────────────────────────────────────
    if (tradeMode === 'paper') {
      const order: Order = {
        id:        generateId(),
        symbol,
        side,
        type,
        size,
        leverage,
        status:    type === 'market' ? 'filled' : 'pending',
        mode:      'paper',
        exchange:  'paper',
        timestamp: Date.now(),
        ...(price !== undefined ? { price } : {}),
      };
      placeOrder(order);
      if (type === 'market') {
        fillOrder(order.id, curPrice);
        openPosition(buildPosition(order, curPrice, stopLoss, takeProfit));
      }
      return order;
    }

    // ── Live mode ──────────────────────────────────────────────────────────
    if (!activeWallet) {
      setLastTradeError('No wallet connected');
      return null;
    }

    setIsSubmitting(true);
    setLastTradeError(null);

    try {
      if (activeWallet.type === 'metamask') {
        // ── Hyperliquid (EVM / MetaMask) ─────────────────────────────────
        const assetIndex = await getAssetIndex(symbol);

        // Market order on HL = IOC with a wide price band
        const isMarket   = type === 'market';
        const slippage   = isMarket ? (side === 'buy' ? 1.05 : 0.95) : 1;
        const limitPrice = isMarket
          ? hlFormatPrice(curPrice * slippage)
          : hlFormatPrice(price ?? curPrice);

        // HL size is in base-asset units (e.g. BTC), not USD notional
        const sizeInBase = (size * leverage) / curPrice;
        const hlSize     = hlFormatSize(sizeInBase, 5);

        const result = await placeHLOrder({
          symbol,
          assetIndex,
          isBuy:      side === 'buy',
          size:       hlSize,
          price:      limitPrice,
          orderType:  isMarket ? 'Ioc' : 'Gtc',
          reduceOnly: false,
        });

        if (!result.success) {
          setLastTradeError(result.error ?? 'Order failed');
          return null;
        }

        // Record in store as live order
        const order: Order = {
          id:        result.hlOrderId ?? generateId(),
          symbol,
          side,
          type,
          size,
          leverage,
          status:    'filled',
          mode:      'live',
          exchange:  'hyperliquid',
          timestamp: Date.now(),
          fillPrice: curPrice,
          ...(price !== undefined ? { price } : {}),
        };
        placeOrder(order);
        fillOrder(order.id, curPrice);
        openPosition(buildPosition(order, curPrice, stopLoss, takeProfit));
        return order;

      } else if (activeWallet.type === 'phantom') {
        // ── Jupiter swap (Solana / Phantom) ──────────────────────────────
        const inputMint  = side === 'buy' ? MINT.USDC  : (symbolToMint(symbol) ?? MINT.USDC);
        const outputMint = side === 'buy' ? (symbolToMint(symbol) ?? MINT.SOL) : MINT.USDC;

        // Amount in smallest units: USDC has 6 decimals, SOL has 9
        const isInputUsdc    = inputMint === MINT.USDC;
        const inputDecimals  = isInputUsdc ? 6 : 9;
        const amountInUnits  = Math.round(size * Math.pow(10, inputDecimals));

        const quote = await getJupiterQuote({
          inputMint,
          outputMint,
          amount: amountInUnits,
          slippageBps: 50,
        });

        const result = await executeJupiterSwap(quote, activeWallet.address);

        if (!result.success) {
          setLastTradeError(result.error ?? 'Swap failed');
          return null;
        }

        const order: Order = {
          id:        generateId(),
          symbol,
          side,
          type:      'market',
          size,
          leverage:  1,
          status:    'filled',
          mode:      'live',
          exchange:  'jupiter',
          timestamp: Date.now(),
          fillPrice: curPrice,
          ...(result.txHash ? { } : {}),
        };
        placeOrder(order);
        fillOrder(order.id, curPrice);
        openPosition(buildPosition(order, curPrice, stopLoss, takeProfit));
        return order;
      }

      setLastTradeError('Unsupported wallet type');
      return null;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setLastTradeError(msg);
      return null;
    } finally {
      setIsSubmitting(false);
    }
  }, [
    tradeMode, activeWallet,
    placeOrder, fillOrder, openPosition,
    // stopLoss/takeProfit come through params — no deps needed
  ]);

  // ── Close position ────────────────────────────────────────────────────────

  const closePos = useCallback((positionId: string) => {
    const pos = posRef.current.find(p => p.id === positionId);
    if (!pos) return;
    const curPrice = pricesRef.current[pos.symbol]?.priceUsd ?? pos.currentPrice;
    closePosition(positionId, curPrice);
  }, [closePosition]);

  // ── Derived values ─────────────────────────────────────────────────────────

  const openOrders         = orders.filter(o => o.status === 'pending');
  const recentFills        = orders.filter(o => o.status === 'filled').slice(0, 20);
  const totalUnrealizedPnl = positions.reduce((s, p) => s + p.unrealizedPnl, 0);
  const winRate            = paperAccount.totalTrades > 0
    ? Math.round(paperAccount.winTrades / paperAccount.totalTrades * 100)
    : 0;

  return {
    tradeMode,
    positions,
    openOrders,
    recentFills,
    paperAccount,
    liveAccount,
    activeWallet,
    totalUnrealizedPnl,
    winRate,
    isSubmitting,
    lastTradeError,
    riskSettings,
    circuitBreakerActive,
    clearTradeError:  () => setLastTradeError(null),
    submitOrder,
    closePosition:    closePos,
    cancelOrder,
    resetPaperAccount,
    setPositionSLTP,
  };
}

// ─── Shared helper ─────────────────────────────────────────────────────────────

function buildPosition(
  order:      Order,
  fillPrice:  number,
  stopLoss?:  number,
  takeProfit?: number,
): Position {
  return {
    id:               generateId(),
    orderId:          order.id,
    symbol:           order.symbol,
    side:             order.side,
    size:             order.size,
    entryPrice:       fillPrice,
    currentPrice:     fillPrice,
    unrealizedPnl:    0,
    unrealizedPnlPct: 0,
    leverage:         order.leverage,
    mode:             order.mode,
    exchange:         order.exchange,
    openedAt:         Date.now(),
    ...(stopLoss   !== undefined ? { stopLoss }   : {}),
    ...(takeProfit !== undefined ? { takeProfit } : {}),
  };
}
